import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, FileText } from 'lucide-react';
function PolicyModal({
  isOpen,
  onClose,
  title,
  icon,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-84">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={onClose} data-id="element-85" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-2xl sm:w-full sm:max-h-[80vh] bg-surface border border-white/10 rounded-2xl z-50 flex flex-col shadow-2xl" data-id="element-86">
            <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-87">
              <div className="flex items-center gap-3" data-id="element-88">
                {icon}
                <h3 className="text-xl font-bold text-white" data-id="element-89">{title}</h3>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-background border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30 transition-colors" data-id="element-90">
                <X className="w-5 h-5" data-id="element-91" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-slate-300 leading-relaxed space-y-4 text-sm sm:text-base" data-id="element-92">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer className="bg-background border-t border-white/5 py-10" data-id="element-93">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-94">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-95">
            <div className="text-center sm:text-left" data-id="element-96">
              <p className="text-sm text-slate-500" data-id="element-97">
                © {new Date().getFullYear()} La Mente Al Desnudo. Todos los
                derechos reservados.
              </p>
            </div>

            <div className="flex items-center gap-6" data-id="element-98">
              <button onClick={() => setPrivacyOpen(true)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-accent transition-colors" data-id="element-99">
                <ShieldCheck className="w-4 h-4" data-id="element-100" />
                Política de Privacidad
              </button>
              <span className="text-slate-700" data-id="element-101">|</span>
              <button onClick={() => setTermsOpen(true)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-accent transition-colors" data-id="element-102">
                <FileText className="w-4 h-4" data-id="element-103" />
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <PolicyModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" icon={<ShieldCheck className="w-6 h-6 text-accent" data-id="element-105" />} data-id="element-104">
        <p className="font-semibold text-white" data-id="element-106">
          Última actualización: Abril 2026
        </p>

        <p data-id="element-107">
          En La Mente Al Desnudo, nos comprometemos a proteger tu privacidad.
          Esta política describe cómo recopilamos, usamos y protegemos tu
          información personal.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-108">
          1. Información que recopilamos
        </h4>
        <p data-id="element-109">
          Al realizar una compra, recopilamos tu nombre y dirección de correo
          electrónico con el único propósito de enviarte el producto digital
          adquirido. No almacenamos información de pago; esta es procesada de
          forma segura por nuestro proveedor de pagos.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-110">
          2. Uso de la información
        </h4>
        <p data-id="element-111">Tu información personal se utiliza exclusivamente para:</p>
        <ul className="list-disc list-inside space-y-1 ml-2" data-id="element-112">
          <li data-id="element-113">Entregar el producto digital a tu correo electrónico.</li>
          <li data-id="element-114">Comunicarnos contigo en caso de problemas con tu pedido.</li>
          <li data-id="element-115">
            Enviarte actualizaciones del producto, solo si has dado tu
            consentimiento.
          </li>
        </ul>

        <h4 className="text-white font-semibold mt-4" data-id="element-116">
          3. Protección de datos
        </h4>
        <p data-id="element-117">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración o destrucción.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-118">4. Terceros</h4>
        <p data-id="element-119">
          No vendemos, intercambiamos ni transferimos tu información personal a
          terceros. Esto no incluye a terceros de confianza que nos asisten en
          la operación de nuestro sitio web, siempre que acuerden mantener esta
          información confidencial.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-120">5. Contacto</h4>
        <p data-id="element-121">
          Si tienes preguntas sobre esta política de privacidad, puedes
          contactarnos a través de nuestro sitio web.
        </p>
      </PolicyModal>

      {/* Terms & Conditions Modal */}
      <PolicyModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" icon={<FileText className="w-6 h-6 text-accent" data-id="element-123" />} data-id="element-122">
        <p className="font-semibold text-white" data-id="element-124">
          Última actualización: Abril 2026
        </p>

        <p data-id="element-125">
          Al acceder y utilizar este sitio web y adquirir nuestros productos
          digitales, aceptas los siguientes términos y condiciones.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-126">1. Producto Digital</h4>
        <p data-id="element-127">
          "La Mente Al Desnudo" es un producto digital en formato PDF. Una vez
          completada la compra, recibirás el archivo en tu correo electrónico de
          forma inmediata. Debido a la naturaleza digital del producto, no se
          aceptan devoluciones una vez entregado.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-128">
          2. Propiedad Intelectual
        </h4>
        <p data-id="element-129">
          Todo el contenido de este manual, incluyendo texto, gráficos, diseño y
          metodologías, está protegido por derechos de autor. Queda prohibida la
          reproducción, distribución o reventa del material sin autorización
          expresa por escrito.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-130">3. Uso Personal</h4>
        <p data-id="element-131">
          El manual es para uso personal y educativo. No debe utilizarse con
          fines de manipulación, acoso o cualquier actividad que viole los
          derechos de otras personas.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-132">
          4. Limitación de Responsabilidad
        </h4>
        <p data-id="element-133">
          La información proporcionada en este manual es de carácter educativo.
          No garantizamos resultados específicos, ya que estos dependen de la
          práctica y aplicación individual de cada usuario.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-134">5. Modificaciones</h4>
        <p data-id="element-135">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones entrarán en vigor inmediatamente después
          de su publicación en este sitio web.
        </p>

        <h4 className="text-white font-semibold mt-4" data-id="element-136">6. Ley Aplicable</h4>
        <p data-id="element-137">
          Estos términos se rigen por las leyes aplicables en la jurisdicción
          correspondiente al vendedor.
        </p>
      </PolicyModal>
    </>;
}